import * as React from 'react';
import { View, Text, Image } from '@react-pdf/renderer';
import PDFDocument, { PDFConfig } from '../base/PDFDocument';
import PDFPatientInfo from '../base/PDFPatientInfo';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles, specializedTypography } from '../styles/typography';
import { PDFClinicalNotesRenderer, PDFSurveyRecordRenderer } from '../utils/contentRenderer';
import { PDFFieldRenderer } from '../utils/PDFFieldRenderer';
import { formatDateForPDF } from '../utils/dataFormatters';
import { createT } from '../utils/simpleTranslate';
import { getRequestStatusName } from '../../../pages/hospital/Request/utils';
import { RequestStatus } from '../../../pages/hospital/Request/types';
import { useResearcherSignature } from '../../../hooks/useResearcherSignature';

// Helper function for researcher names
const formatResearcherName = (researcher: IResearcher): string => {
  return `${researcher.name} ${researcher.surnames}`.trim();
};

/**
 * Request PDF Document Component
 * 
 * Example migration of RequestPDF to @react-pdf/renderer.
 * Demonstrates HTML content handling for clinical notes and survey records.
 */

interface IPatient {
  personalData: {
    name?: string;
    surnames?: string;
    birthdate?: string | Date;
    sex?: string;
    phone?: string;
    healthId?: string;
    nationalId?: string;
    passportNumber?: string;
  };
}

interface IResearcher {
  uuid: string;
  name: string;
  surnames: string;
}

interface IDepartment {
  uuid?: string | undefined;
  name: string;
}

interface RequestData {
  id: string;
  type: number;
  status?: number;
  createdAt: string;
  completionDate?: string;
  researcher?: IResearcher;
  departmentRequest?: IDepartment;
  typeSpecificData?: {
    notes?: string; // HTML content
  };
  submissionPatient?: {
    // Keep the interface but don't use surveyRecords from here
  };
  requestsServiceInvestigation?: Array<{
    serviceInvestigation: {
      service: { name: string };
      description: string;
    };
    patientInvestigation?: {
      records?: {
        surveys?: Array<{
          submissions?: Array<{
            surveyRecords?: Array<{
              surveyField: { 
                label: string;
                name?: string;
                type?: string;
              };
              value: any; // Can be string or array with template data
            }>;
          }>;
        }>;
      };
    };
  }>;
  pharmacyItems?: Array<{
    name: string;
  }>;
}

interface SurveyRecord {
  value: string; // HTML content
  surveySection: {
    name: string;
  };
}

interface RequestPDFDocumentProps {
  request: RequestData;
  patient: IPatient;
  submission?: any; // Optional submission data with surveyRecords
  config: PDFConfig;
  locale?: string;
  uuidInvestigation: string; // Investigation UUID for signature fetching
}

export const RequestPDFDocument: React.FC<RequestPDFDocumentProps> = ({
  request,
  patient,
  submission,
  config,
  locale = 'fr',
  uuidInvestigation,
}) => {
  // Extract prescribingDoctor and department from request object
  const prescribingDoctor = request.researcher;
  const department = request.departmentRequest;
  const t = createT(locale);

  // Fetch researcher signature
  const { 
    signatureUrl, 
    loading: loadingSignature, 
    error: signatureError 
  } = useResearcherSignature({
    uuidInvestigation,
    uuidResearcher: prescribingDoctor?.uuid || '',
    enabled: !!prescribingDoctor?.uuid, // Only fetch if researcher exists
  });
  const renderRequestDetails = () => (
    <View>
      {/* Left Column */}
      <View style={{ paddingRight: 2 }}>
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {t('pages.request.pdf.request_id')}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{request.id}
            </Text>
          </Text>
        </View>
        
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {t('pages.request.pdf.date_of_request')}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{formatDateForPDF(request.createdAt, locale)}
            </Text>
          </Text>
        </View>
        
        {request.completionDate && (
        <View style={{ marginBottom: 3 }}>
          <Text>
            <Text style={specializedTypography.fieldLabel}>
              {t('pages.request.pdf.completion_date')}:
            </Text>
            <Text style={specializedTypography.fieldValue}>
              {' '}{formatDateForPDF(request.completionDate, locale)}
            </Text>
          </Text>
        </View>
        )}

        {request.status !== undefined && (
          <View style={{ marginBottom: 1 }}>
            <Text>
                <Text style={specializedTypography.fieldLabel}>
                {t('pages.request.pdf.status')}: 
                </Text>
                <Text style={specializedTypography.fieldValue}>
                {' '}{t(`pages.hospital.services.request_status.${getRequestStatusName(request.status as RequestStatus)}`)}
                </Text>
            </Text>
          </View>
        )}

        {prescribingDoctor && (
          <View style={{ marginBottom: 3 }}>
            <Text>
                <Text style={specializedTypography.fieldLabel}>
                {t('pages.request.pdf.requested_by')}:
                </Text>
                <Text style={specializedTypography.fieldValue}>
                {' '}{formatResearcherName(prescribingDoctor)}
                </Text>
            </Text>
          </View>
        )}
        
        {department && (
          <View style={{ marginBottom: 1 }}>
            <Text>
                <Text style={specializedTypography.fieldLabel}>
                {t('pages.request.pdf.department')}:
                </Text>
                <Text style={specializedTypography.fieldValue}>
                {' '}{department.name}
                </Text>
            </Text>
          </View>
        )}
      </View>
    </View>
  );
  
  const renderPatientAndRequestInfo = () => (
    <View style={{ flexDirection: 'row', width: '100%' }}>
      <View style={{ width: '50%' }}>
        <PDFPatientInfo patient={patient} locale={locale} />
      </View>
      <View style={{ width: '50%' }}>
        {renderRequestDetails()}
      </View>
    </View>
  );

  const renderClinicalNotes = () => {
    if (!request.typeSpecificData?.notes) return null;

    return (
      <View style={[commonPDFStyles.section]}>
        <Text style={typographyStyles.h4}>
          {t('pages.request.pdf.clinical_notes')}
        </Text>
        <View style={commonPDFStyles.sectionContent}>
          <PDFClinicalNotesRenderer 
            notes={request.typeSpecificData.notes || ''}
            fallbackStrategy="plaintext"
          />
        </View>
      </View>
    );
  };

  const renderSurveyRecords = () => {
    // Use submission prop if provided, otherwise try to extract from request
    const investigationRecords: any[] = [];
    if (submission?.surveyRecords) {
      // Use the submission prop directly
      submission.surveyRecords.forEach((record: any) => {
        // Handle template medical history records with array values
        if (record.surveyField.type === 'template_medical_history' && Array.isArray(record.value)) {
          record.value.forEach((templateData: any) => {
            if (templateData['template_medical_history-notes']) {
              investigationRecords.push({
                surveyField: {
                  ...record.surveyField,
                  name: 'template_medical_history-notes',
                  type: 'template_medical_history-notes'
                },
                value: templateData['template_medical_history-notes']
              });
            }
          });
        } else {
          investigationRecords.push(record);
        }
      });
    }
    
    console.log('investigationRecords extracted:', investigationRecords.length); 
    //else {
    //   // Fallback: Collect survey records from requestsServiceInvestigation
    //   request.requestsServiceInvestigation?.forEach((reqService) => {
    //     reqService.patientInvestigation?.records?.surveys?.forEach((survey) => {
    //       survey.submissions?.forEach((submissionData) => {
    //         submissionData.surveyRecords?.forEach((record) => {
    //           // Handle template medical history records with array values
    //           if (record.surveyField.type === 'template_medical_history' && Array.isArray(record.value)) {
    //             record.value.forEach((templateData: any) => {
    //               if (templateData['template_medical_history-notes']) {
    //                 investigationRecords.push({
    //                   surveyField: {
    //                     ...record.surveyField,
    //                     name: 'template_medical_history-notes',
    //                     type: 'template_medical_history-notes'
    //                   },
    //                   value: templateData['template_medical_history-notes']
    //                 });
    //               }
    //             });
    //           } else {
    //             investigationRecords.push(record);
    //           }
    //         });
    //       });
    //     });
    //   });
    // }

    if (!investigationRecords.length) return null;

    // Filter out template_medical_history-template_id records and 
    // template_medical_history records with null/empty values
    const filteredRecords = investigationRecords.filter(
      (surveyRecord) => {
        // Filter out template_medical_history-template_id type
        if (surveyRecord.surveyField.type === 'template_medical_history-template_id') {
          return false;
        }
        
        // Filter out template_medical_history type with null or empty values
        if (surveyRecord.surveyField.type === 'template_medical_history' && 
            (!surveyRecord.value || surveyRecord.value === null)) {
          return false;
        }

        return true;
      }
    );

    if (!filteredRecords.length) return null;

    return (
      <View style={commonPDFStyles.section}>
        {filteredRecords.map((surveyRecord, index) => (
          <View key={index} style={[commonPDFStyles.marginBottom]} wrap={surveyRecord.surveyField.name === 'template_medical_history-notes'}>
            {surveyRecord.surveyField.name !== 'template_medical_history-notes' && (
              <Text style={typographyStyles.h4}>
                {surveyRecord.surveyField.label}:
              </Text>
            )}
            <View style={commonPDFStyles.sectionContent}>
              {surveyRecord.surveyField.name === 'template_medical_history-notes' ? (
                <PDFClinicalNotesRenderer 
                  notes={surveyRecord.value || ''}
                  fallbackStrategy="plaintext"
                />
              ) : (
                <PDFFieldRenderer
                  field={{
                    id: surveyRecord.surveyField.name || index,
                    name: surveyRecord.surveyField.name || '',
                    label: surveyRecord.surveyField.label || '',
                    type: surveyRecord.surveyField.type || 'text',
                  }}
                  value={surveyRecord.value}
                  locale={locale}
                />
              )}
            </View>
          </View>
        ))}
      </View>
    );
  };

  const renderServiceInvestigations = () => {
    if (!request.requestsServiceInvestigation?.length) return null;

    return (
      <View style={[commonPDFStyles.section]}>
        <Text style={typographyStyles.h4}>
          {t('pages.request.pdf.services')}:
        </Text>
        <View style={commonPDFStyles.sectionContent}>
          {request.requestsServiceInvestigation.map((item, index) => (
            <Text key={index} style={typographyStyles.body2}>
              • {item.serviceInvestigation.description}
            </Text>
          ))}
        </View>
      </View>
    );
  };

  const renderPharmacyItems = () => {
    if (!request.pharmacyItems?.length) return null;

    return (
      <View style={[commonPDFStyles.section]}>
        <Text style={typographyStyles.h4}>
          {t('pages.request.pdf.pharmacy_items')}
        </Text>
        <View style={commonPDFStyles.sectionContent}>
          {request.pharmacyItems.map((item, index) => (
            <Text key={index} style={typographyStyles.body2}>
              • {item.name}
            </Text>
          ))}
        </View>
      </View>
    );
  };

  const renderFooter = () => {
    if (!prescribingDoctor) return null;

    return (
      <View style={[commonPDFStyles.signature, commonPDFStyles.pageBreakAvoid]}>
        {/* Signature placeholder (label + name + title) */}
        <Text style={commonPDFStyles.signatureName}>
          {t('general.signature')}
        </Text>
        <Text style={commonPDFStyles.signatureName}>
          {formatResearcherName(prescribingDoctor)}
        </Text>
        <Text style={commonPDFStyles.signatureTitle}>
          {t('hospital.billing.prescribingDoctor')}
        </Text>

        {/* Signature image below placeholder */}
        {signatureUrl && !signatureError && (
          <View style={{ marginTop: 10, alignItems: 'center' }}>
            <Image 
              src={signatureUrl} 
              style={{ 
                width: 150, 
                height: 75, 
                objectFit: 'contain',
              }} 
            />
          </View>
        )}
      </View>
    );
  };

  return (
    <PDFDocument
      title={t('pages.request.pdf.title')}
      patientInfo={renderPatientAndRequestInfo()}
      config={config}
      locale={locale}
      footer={renderFooter()}
      metadataFooter={{
        patient,
        documentReference: String(request.id),
        documentDate: request.createdAt,
        validatedBy: prescribingDoctor
          ? {
              name: formatResearcherName(prescribingDoctor),
              title: t('hospital.billing.prescribingDoctor'),
            }
          : undefined,
        locale,
        hospitalName: config.hospitalName,
        city: config.city,
      }}
    >
      {renderServiceInvestigations()}
      {renderClinicalNotes()}
      {renderSurveyRecords()}
      {renderPharmacyItems()}
    </PDFDocument>
  );
};

export default RequestPDFDocument;
